import {
  Button,
  ButtonGroup,
  Checkbox,
  Input,
  Modal,
  Radio,
  RadioGroup,
  Select,
  SelectOption,
  Spacer,
  Stack,
  StatusCard,
  TextBody,
  TextCaption,
  Textarea,
  TextSubtitle,
  TextTitle,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import {
  hasPypestreamPrefix,
  PYPESTREAM_PREFIX,
  secretNameError,
  valueOf,
} from '@pypestream/ui-patterns';
import { type ReactNode, useEffect, useState } from 'react';

import { DestructiveDialog } from '../../../../components/modals/destructive-dialog/destructive-dialog';
import { KeyAudiencePicker } from './key-audience-picker';
import {
  createOrgSecret,
  type KeyAudience,
  type MaestroFailure,
  type OrgSecret,
  type OrgSecretUpdate,
  reasonFor,
  rotateOrgSecret,
  type ScopeProject,
  updateOrgSecret,
} from './maestro-org-api';
import {
  moveOptions,
  type ScopeOption,
  scopeInput,
  scopeTarget,
  type ScopeTarget,
} from './org-secret-scope';
import { takenMessage, useSecretsText } from './org-secrets-text';
import { useScopeOptionLabel } from './org-secrets-toolbar';
import './maestro-org.scss';

export type OrgSecretModalMode =
  | { type: 'add' }
  /** Description, scope and, for a Super Admin on the root org, the Pypestream key mark. */
  | { type: 'edit'; secret: OrgSecret }
  | { type: 'value'; secret: OrgSecret };

const ALL_ORGS: KeyAudience = { mode: 'all', accountIds: [] };

// DS Input has no label of its own; the control also carries `aria-label`.
const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="c-org-field">
    <TextSubtitle size="small" variant="secondary">
      {label}
    </TextSubtitle>
    {children}
  </label>
);

const sameAudience = (a: KeyAudience | null, b: KeyAudience) =>
  a !== null &&
  a.mode === b.mode &&
  a.accountIds.length === b.accountIds.length &&
  a.accountIds.every((id) => b.accountIds.includes(id));

/** Add an item, edit one, or replace its value; marking a Pypestream key is confirmed with its audience. */
export function OrgSecretModal({
  orgId,
  mode,
  isRootOrg,
  canMarkPypestreamKey,
  scopes,
  projects,
  defaultScope,
  onClose,
  onSaved,
}: {
  orgId: string;
  mode: OrgSecretModalMode | null;
  /** The Pypestream org: the only one whose names may start with `pypestream_`. */
  isRootOrg: boolean;
  /** A Super Admin on the root org: the only one who marks, unmarks or edits a key. */
  canMarkPypestreamKey: boolean;
  scopes: ScopeOption[];
  projects: ScopeProject[];
  /** Spec 6.4: Organization, or the scope the list is filtered to. */
  defaultScope: ScopeTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const text = useSecretsText();
  const optionLabel = useScopeOptionLabel();
  const [kind, setKind] = useState<OrgSecret['kind']>('secret');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [shown, setShown] = useState(false);
  const [pypestreamKey, setPypestreamKey] = useState(false);
  const [target, setTarget] = useState<ScopeTarget>('org');
  const [audience, setAudience] = useState<KeyAudience>(ALL_ORGS);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [unmarking, setUnmarking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MaestroFailure | null>(null);

  useEffect(() => {
    const secret = mode?.type === 'add' ? null : (mode?.secret ?? null);
    setKind(secret?.kind ?? 'secret');
    setName('');
    setDescription(mode?.type === 'edit' ? (secret?.description ?? '') : '');
    setValue('');
    setShown(false);
    setPypestreamKey(secret?.pypestreamKey ?? false);
    setTarget(secret ? scopeTarget(secret.scope) : defaultScope);
    setAudience(secret?.audience ?? ALL_ORGS);
    setAudienceCount(null);
    setConfirming(false);
    setUnmarking(false);
    setError(null);
    // Reset once per opening, not when the filter behind it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const adding = mode?.type === 'add';
  const editing = mode?.type === 'edit' ? mode.secret : null;
  const rotating = mode?.type === 'value' ? mode.secret : null;
  const variable = kind === 'variable';
  const wasKey = editing?.pypestreamKey ?? false;
  const itemName = editing?.name ?? name;
  const orgScoped = target === 'org';
  const canToggleKey =
    canMarkPypestreamKey && orgScoped && (adding || editing !== null);
  const marking = pypestreamKey && !wasKey;
  const audienceChanged =
    wasKey &&
    pypestreamKey &&
    !sameAudience(editing?.audience ?? null, audience);

  const nameCode = adding
    ? (secretNameError(name, { allowPypestreamPrefix: isRootOrg }) ??
      (pypestreamKey && name && !hasPypestreamPrefix(name)
        ? 'keyPrefix'
        : null))
    : null;
  const keyNameMissing =
    editing !== null && pypestreamKey && !hasPypestreamPrefix(editing.name);
  const taken =
    error?.reason === 'name_taken' && error.existing
      ? takenMessage(text, error.existing)
      : null;
  const ready = adding
    ? Boolean(value) && Boolean(name) && !nameCode
    : editing
      ? !keyNameMissing && (!audienceChanged || audienceCount !== null)
      : Boolean(value);
  const options = editing
    ? moveOptions(scopes, scopeTarget(editing.scope))
    : scopes;
  const moved = editing !== null && target !== scopeTarget(editing.scope);

  const togglePypestreamKey = () => {
    const next = !pypestreamKey;
    setPypestreamKey(next);
    if (next) setTarget('org');
    if (next && adding && !hasPypestreamPrefix(name))
      setName(`${PYPESTREAM_PREFIX}${name}`);
    if (!next && adding && name === PYPESTREAM_PREFIX) setName('');
  };

  const run = async (write: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await write();
      onSaved();
      return true;
    } catch (e) {
      setError(reasonFor(e));
      setConfirming(false);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const editInput = (): OrgSecretUpdate => ({
    description,
    ...(moved ? { scope: scopeInput(target, projects) } : {}),
    ...(pypestreamKey !== wasKey ? { pypestreamKey } : {}),
    ...(marking || audienceChanged ? { audience } : {}),
  });

  const save = async () => {
    if (!ready || busy) return;
    if (rotating) {
      await run(() => rotateOrgSecret(orgId, rotating.id, value));
      return;
    }
    if (editing && wasKey && !pypestreamKey) {
      setUnmarking(true);
      return;
    }
    if ((adding && pypestreamKey) || marking) {
      if (!confirming) {
        setConfirming(true);
        return;
      }
      if (audienceCount === null) return;
    }
    if (editing) {
      await run(() => updateOrgSecret(orgId, editing.id, editInput()));
      return;
    }
    await run(() =>
      createOrgSecret(orgId, {
        name,
        kind,
        value,
        description: description || undefined,
        pypestreamKey,
        scope: scopeInput(pypestreamKey ? 'org' : target, projects),
        ...(pypestreamKey ? { audience } : {}),
      })
    );
  };

  const title = confirming
    ? text('confirmKey.title', { name: itemName })
    : editing
      ? text('modal.editItemTitle', { name: editing.name })
      : rotating
        ? text(variable ? 'modal.editTitle' : 'modal.rotateTitle', {
            name: rotating.name,
          })
        : text('modal.addTitle');
  const masked = !variable && !shown;

  const scopeField = (
    <Field label={text('modal.scope')}>
      {pypestreamKey ? (
        <TextCaption variant="secondary">
          {text('modal.scopeKeyHelp')}
        </TextCaption>
      ) : (
        <Select
          variant="outlined"
          aria-label={text('modal.scope')}
          value={target}
          test-id="org-secret-scope"
          onChange={(e) => {
            const next = options.find((o) => o.value === valueOf(e));
            if (next) setTarget(next.value);
          }}
        >
          {options.map((option) => (
            <SelectOption key={option.value} value={option.value}>
              {optionLabel(option)}
            </SelectOption>
          ))}
        </Select>
      )}
      {!pypestreamKey && (
        <TextCaption variant="secondary">{text('modal.scopeHelp')}</TextCaption>
      )}
    </Field>
  );

  const keyToggle = canToggleKey && (
    <div>
      <Checkbox
        checked={pypestreamKey}
        disabled={
          editing !== null && !wasKey && !hasPypestreamPrefix(editing.name)
        }
        test-id="org-secret-pypestream-key"
        onChange={togglePypestreamKey}
      >
        {text('modal.pypestreamKey')}
      </Checkbox>
      <TextCaption variant="secondary">
        {editing && !hasPypestreamPrefix(editing.name)
          ? text('modal.errors.keyPrefix')
          : text('modal.pypestreamKeyHelp')}
      </TextCaption>
    </div>
  );

  return (
    <>
      <Modal
        open={mode !== null && !unmarking}
        size="medium"
        test-id="org-secret-modal"
        onClose={onClose}
      >
        <TextTitle size="small" slot="header">
          {title}
        </TextTitle>
        {confirming ? (
          <Stack direction="column" gutter="small" test-id="org-secret-confirm">
            <TextBody>{text('confirmKey.body', { name: itemName })}</TextBody>
            <KeyAudiencePicker
              accountId={orgId}
              audience={audience}
              onChange={setAudience}
              onCount={setAudienceCount}
            />
            <StatusCard status="warning" body={text('confirmKey.warn')} />
          </Stack>
        ) : (
          <Stack direction="column" gutter="medium">
            {adding && (
              <>
                <RadioGroup
                  label={text('modal.type')}
                  value={kind}
                  orientation="horizontal"
                  onChange={(e) => setKind(valueOf(e) as OrgSecret['kind'])}
                >
                  {(['secret', 'variable'] as const).map((option) => (
                    <Radio key={option} value={option}>
                      {text(`modal.${option}`)}
                      <TextBody size="small" variant="secondary">
                        {text(`modal.${option}Help`)}
                      </TextBody>
                    </Radio>
                  ))}
                </RadioGroup>
                <Field label={text('modal.name')}>
                  <Input
                    variant="outlined"
                    aria-label={text('modal.name')}
                    helpText={
                      nameCode
                        ? text(`modal.errors.${nameCode}`)
                        : (taken ?? text('modal.nameHelp'))
                    }
                    hasError={Boolean(nameCode || taken)}
                    placeholder={text('modal.namePlaceholder')}
                    autocomplete="off"
                    value={name}
                    test-id="org-secret-name"
                    onInput={(e) => setName(valueOf(e))}
                  />
                </Field>
              </>
            )}
            {(adding || editing) && (
              <Textarea
                variant="outlined"
                rows={2}
                label={text('modal.description')}
                placeholder={text('modal.descriptionPlaceholder')}
                value={description}
                onInput={(e) => setDescription(valueOf(e))}
              />
            )}
            {(adding || editing) && scopeField}
            {moved && (
              <StatusCard
                status="warning"
                label={text('modal.moveWarnTitle')}
                body={text('modal.moveWarn', { name: itemName })}
              />
            )}
            {!editing && (
              <Field label={text(rotating ? 'modal.newValue' : 'modal.value')}>
                <div className="c-org-modal__value">
                  <Input
                    className={
                      masked ? 'c-org-modal__input--masked' : undefined
                    }
                    variant="outlined"
                    aria-label={text(
                      rotating ? 'modal.newValue' : 'modal.value'
                    )}
                    helpText={
                      rotating && !variable
                        ? text('modal.rotateHelp', { name: rotating.name })
                        : undefined
                    }
                    placeholder={text('modal.valuePlaceholder')}
                    disableAutofill
                    autocomplete="off"
                    value={value}
                    test-id="org-secret-value"
                    onInput={(e) => setValue(valueOf(e))}
                  />
                  {!variable && (
                    <Button
                      variant="secondary"
                      size="small"
                      pressed={shown}
                      label={text('modal.showLabel')}
                      test-id="org-secret-reveal"
                      onClick={() => setShown(!shown)}
                    >
                      {text(shown ? 'modal.hide' : 'modal.show')}
                    </Button>
                  )}
                </div>
              </Field>
            )}
            {keyToggle}
            {editing && wasKey && pypestreamKey && (
              <KeyAudiencePicker
                accountId={orgId}
                audience={audience}
                onChange={setAudience}
                onCount={setAudienceCount}
              />
            )}
            {isRootOrg && adding && !pypestreamKey && orgScoped && (
              <TextCaption variant="secondary" test-id="org-secret-not-shared">
                {text('modal.notShared')}
              </TextCaption>
            )}
            {!editing && (
              <StatusCard
                status={variable ? 'warning' : 'info'}
                label={text(
                  variable ? 'modal.variableNoteTitle' : 'modal.secretNoteTitle'
                )}
                body={text(
                  variable ? 'modal.variableNote' : 'modal.secretNote'
                )}
              />
            )}
          </Stack>
        )}
        {error && !(adding && taken) && (
          <>
            <Spacer size="small" />
            <TextCaption variant="danger" role="alert">
              {taken ??
                t(`manager/organizations:orgTabs.errors.${error.reason}`)}
            </TextCaption>
          </>
        )}
        <ButtonGroup slot="footer">
          <Button
            variant="secondary"
            onClick={() => (confirming ? setConfirming(false) : onClose())}
          >
            {text(confirming ? 'confirmKey.back' : 'modal.cancel')}
          </Button>
          <Button
            disabled={!ready || (confirming && audienceCount === null)}
            loading={busy}
            test-id="org-secret-save"
            onClick={() => void save()}
          >
            {text(
              confirming
                ? 'confirmKey.confirm'
                : editing
                  ? 'modal.save'
                  : rotating
                    ? variable
                      ? 'modal.saveValue'
                      : 'modal.rotate'
                    : 'modal.add'
            )}
          </Button>
        </ButtonGroup>
      </Modal>
      <DestructiveDialog
        open={unmarking}
        title={text('unmarkKey.title', { name: itemName })}
        confirmLabel={text('unmarkKey.confirm')}
        cancelLabel={text('delete.cancel')}
        testId="org-secret-unmark"
        onCancel={() => setUnmarking(false)}
        onConfirm={async () => {
          if (!editing) return false;
          return run(() => updateOrgSecret(orgId, editing.id, editInput()));
        }}
      >
        <TextBody>{text('unmarkKey.body')}</TextBody>
        <StatusCard
          status="error"
          body={text('unmarkKey.warn', { name: itemName })}
        />
        {error && (
          <TextCaption variant="danger" role="alert">
            {t(`manager/organizations:orgTabs.errors.${error.reason}`)}
          </TextCaption>
        )}
      </DestructiveDialog>
    </>
  );
}
